import sortBy from 'lodash/sortBy';

import { ISelectOption } from '@crm-framework/types';
import { IBusinessUnitRights } from '@crm-framework/auth';

import {
  DictionaryMapperKeyNameType,
  DictionaryMapperKeyIdType,
} from './types';
import {
  IBaseDictionary,
  IBusinessUnitModel,
  ICountryModel,
  IEnvironmentModel,
} from './models';

export const dictionaryNameMapper = <T>(
  dictionary: T[],
  keyForName: DictionaryMapperKeyNameType<T>
) =>
  dictionary.map((data) => ({
    ...data,
    name: data[keyForName],
  }));

export const dictionaryIdMapper = <T>(
  dictionary: T[],
  keyForId: DictionaryMapperKeyIdType<T>
) =>
  dictionary.map((data) => ({
    ...data,
    id: data[keyForId],
  }));

interface IGetValueOfKeyById<TFallback, TArray, TKey> {
  id: number | null | undefined;
  arrayForSearch: TArray;
  keyName: TKey;
  fallback?: TFallback;
}

export const getValueOfKeyById = <
  TFallback = undefined,
  TArray extends any[] = any[],
  TKey extends keyof TArray[number] = keyof TArray[number]
>({
  id,
  arrayForSearch,
  keyName,
  fallback,
}: IGetValueOfKeyById<TFallback, TArray, TKey>):
  | TFallback
  | TArray[number][TKey] => {
  if (!id) return fallback;

  const dataById = arrayForSearch.find((data) => id === data.id);

  return dataById ? dataById[keyName] : fallback;
};

interface IGetBUByUserBURights {
  businessUnitsRights: IBusinessUnitRights;
  rightForCheck: string;
  skipAllRight?: boolean;
  businessUnits: IBusinessUnitModel[];
}

export const getBUByUserBURights = ({
  businessUnitsRights,
  rightForCheck,
  skipAllRight = false,
  businessUnits,
}: IGetBUByUserBURights): IBusinessUnitModel[] => {
  // NOTE: Konstantin Lupashku said check "all" key, if it key is present return all BU
  if (businessUnitsRights.all && !skipAllRight) {
    return businessUnits;
  }

  const availableBUIds = businessUnitsRights[rightForCheck];

  if (!availableBUIds) {
    return [];
  }

  return businessUnits.filter((item) => {
    const { id, environmentId } = item;

    if (environmentId) {
      return availableBUIds.some((availableBUId) => id === availableBUId);
    }

    return false;
  });
};

export const filteringEnvironmentsByBUs = (
  environments: IEnvironmentModel[],
  businessUnits: IBusinessUnitModel[]
) =>
  environments.filter(({ id: environmentId }) =>
    businessUnits.some(
      (businessUnit) => businessUnit.environmentId === environmentId
    )
  );

export const mapBUbyEnvironments = (
  environment: IEnvironmentModel[],
  businessUnits: IBusinessUnitModel[]
) =>
  environment.map(({ id, name }) => ({
    label: name || '(default)',
    options: businessUnits.filter(({ environmentId }) => environmentId === id),
  }));

export const mapDictionaryToSelectOptions = (dictionary: IBaseDictionary[]) =>
  dictionary.map<ISelectOption>(({ id, name }) => ({
    label: name,
    value: id,
  }));

export const getCountriesWithoutUnknown = (countries: ICountryModel[]) =>
  countries.filter((country) => country.name.toLowerCase() !== 'unknown');

export const getSortedDictionary = <T>(
  dictionary: T[],
  order: string | ((dictionary: T[]) => T[])
) =>
  typeof order !== 'string'
    ? order(dictionary)
    : sortBy(dictionary, (dictionaryItem: T) => dictionaryItem[order]);
